import { createFileRoute } from '@tanstack/react-router';
import { useOrganization } from '@/hooks/useOrganization';
import { useOrganizationMembers, type Member } from '@/hooks/useOrganizationMembers';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { PlusIcon, UsersIcon, ShieldIcon, HomeIcon, BuildingIcon } from 'lucide-react';
import { InviteMemberModal } from '@/components/rbac/InviteMemberModal';
import { InvitationsList } from '@/components/rbac/InvitationsList';
import { EditMemberModal } from '@/components/rbac/EditMemberModal';
import { PageHeader } from '@/components/layout/PageHeader';
import { useState } from 'react';
import { loadAuthGuard } from '@/lib/auth-helpers';

export const Route = createFileRoute('/_authenticated/org/$orgId/access')({
  loader: loadAuthGuard<{ orgId: string }>(async ({ params, context, ensureQueryData, sessionReady }) => {
    const { orgId } = params;
    const { trpc } = context;

    if (!sessionReady) {
      return { orgId };
    }
    
    // Preload organizations to get organizationId from slug
    const orgsData = await ensureQueryData(
      trpc.organizations.getAll.queryOptions(undefined),
    );
    
    // Find organization by slug
    const orgs = (orgsData as { organizations?: Array<{ id: string; slug: string }> })?.organizations || [];
    const organization = orgs.find((o) => o.slug === orgId || o.id === orgId);
    const organizationId = organization?.id;
    
    // Preload organization members if we have organizationId
    if (organizationId) {
      await ensureQueryData(
        trpc.organizationMembers.getMembers.queryOptions({ organizationId }),
      );
    }
    
    return { orgId };
  }),
  component: OrganizationAccessPage,
});

function OrganizationAccessPage() {
  const { orgId } = Route.useParams();
  const { organization, isLoading } = useOrganization(orgId);
  const [inviteOpen, setInviteOpen] = useState(false);
  const [editMemberOpen, setEditMemberOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);

  const { data: membersData, isLoading: membersLoading } = useOrganizationMembers(organization?.slug || '');
  const members: Member[] = membersData?.members || [];

  const handleEditMember = (member: Member) => {
    setSelectedMember(member);
    setEditMemberOpen(true);
  };

  if (isLoading || membersLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader variant="spinner" size="lg" color="accent" text="Loading access control..." />
      </div>
    );
  }

  const breadcrumbs = [
    {
      name: 'Dashboard',
      to: '/dashboard',
      icon: HomeIcon,
    },
    {
      name: organization?.name || 'Organization',
      to: `/org/${organization?.slug || orgId}`,
      icon: BuildingIcon,
    },
    {
      name: 'Access',
      to: `/org/${organization?.slug || orgId}/access`,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        breadcrumbs={breadcrumbs}
        title="Access Control"
        subtitle={organization ? `Manage members and their permissions in ${organization.name}` : undefined}
        action={
          <Button data-testid="invite-member-button" onClick={() => setInviteOpen(true)}>
            <PlusIcon className="w-4 h-4 mr-2" />
            Invite Member
          </Button>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Members</CardTitle>
            <UsersIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{members.length}</div>
            <p className="text-xs text-muted-foreground">Active users</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Admins</CardTitle>
            <ShieldIcon className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {members.filter((m: Member) => m.role === 'admin').length}
            </div>
            <p className="text-xs text-muted-foreground">With full access</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
          <CardDescription>People with access to this organization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {members.map((member: Member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-lg"
              >
                <div>
                  <h3 className="text-sm font-medium text-gray-900">{member.name || member.email}</h3>
                  <p className="text-sm text-gray-500">{member.email}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                    {member.role}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    data-testid={`edit-member-${member.id}`}
                    onClick={() => handleEditMember(member)}
                  >
                    Edit
                  </Button>
                </div>
              </div>
            ))}
            {members.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-lg font-medium text-gray-900 mb-2">No members yet</h3>
                <p className="text-gray-500 mb-4">Invite members to collaborate</p>
                <Button onClick={() => setInviteOpen(true)}>
                  <PlusIcon className="w-4 h-4 mr-2" />
                  Invite Member
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {organization?.slug && (
        <InvitationsList organizationSlug={organization.slug} />
      )}

      {organization?.id && (
        <InviteMemberModal organizationId={organization.id} open={inviteOpen} onOpenChange={setInviteOpen} />
      )}

      {organization?.id && selectedMember && (
        <EditMemberModal
          open={editMemberOpen}
          onOpenChange={setEditMemberOpen}
          member={selectedMember}
          organizationId={organization.id}
          onSuccess={() => {
            // Members list will refresh automatically via query invalidation
            setSelectedMember(null);
          }}
        />
      )}
    </div>
  );
}

